import SmallLoader from '@/src/Common/components/SmallLoader';
import useLibrary from './hooks/useLibrary';
import LibraryList from './LibraryList';
import { Heading } from '@/src/tailwindui/heading';
import {
    XMarkIcon,
    ListBulletIcon,
    Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/src/lib/utils';
import { Input, InputGroup } from '@/src/tailwindui/input';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import LibraryGrid from './LibraryGrid';
export default function Library({
    onImageSelected,
    onClose,
}: {
    onImageSelected: (image: string) => void;
    onClose: (e: any) => void;
}) {
    const { filteredLibrary, isLoading, search, setSearch } = useLibrary();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [view, setView] = useState<'list' | 'grid'>('grid');

    useEffect(() => {
        if (isFocused) {
            inputRef.current?.focus();
        }
    }, [isFocused]);

    return (
        <div
            className="bg-[#FFFFFF] dark:bg-[#262C2C] shadow-sm sm:rounded-lg w-[90%] lg:w-[80%] relative pb-[40px]"
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-start lg:items-center ">
                    <div className="flex flex-col lg:flex-row gap-2">
                        <Heading className="text-3xl plex-sans text-[#000000] dark:text-white">
                            Asset Library
                        </Heading>
                        <div className="flex max-w-xl gap-4 lg:mx-4">
                            <div
                                className={cn(
                                    'flex flex-row search-input-wrapper search-flex-row transition-all duration-300',
                                    'w-[220px] lg:w-[300px] hidden  lg:block',
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsFocused(true);
                                }}
                            >
                                <InputGroup>
                                    <MagnifyingGlassIcon data-slot="icon" />
                                    <Input
                                        name="search"
                                        ref={inputRef}
                                        placeholder="Search the library&hellip;"
                                        className="search-input monospace-font"
                                        value={search}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                    />
                                </InputGroup>
                            </div>
                        </div>
                    </div>
                    <div className="flex-1"></div>
                    <div className="flex flex-row gap-2 mr-6 mt-[-6px]">
                        <Squares2X2Icon
                            className={cn(
                                'w-10 h-10 text-[#000000] dark:text-white cursor-pointer p-2',
                                view === 'grid' &&
                                    'bg-[#F4F4F1] dark:bg-black/90 rounded-md',
                            )}
                            onClick={() => setView('grid')}
                        />
                        <ListBulletIcon
                            className={cn(
                                'w-10 h-10 text-[#000000] dark:text-white cursor-pointer p-2',
                                view === 'list' &&
                                    'bg-[#F4F4F1] dark:bg-black/90 rounded-md',
                            )}
                            onClick={() => setView('list')}
                        />
                    </div>
                    <XMarkIcon
                        className="w-6 h-6 text-[#000000] dark:text-white cursor-pointer"
                        onClick={onClose}
                    />
                </div>
                <div
                    className={cn(
                        'flex flex-row search-input-wrapper search-flex-row transition-all duration-300',
                        'lg:w-full block  lg:hidden',
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsFocused(true);
                    }}
                >
                    <InputGroup>
                        <MagnifyingGlassIcon data-slot="icon" />
                        <Input
                            name="search"
                            ref={inputRef}
                            placeholder="Search the library&hellip;"
                            className="search-input monospace-font"
                            value={search}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>
                </div>
                <div className="mt-5 transition-all duration-300 relative">
                    {isLoading ? (
                        <div className="flex justify-center items-center h-[400px]">
                            <SmallLoader />
                        </div>
                    ) : filteredLibrary.length === 0 ? (
                        <div className="flex justify-center items-center h-full">
                            <p className="text-[#333333] dark:text-white h-[400px] plex-sans flex flex-col gap-2 items-center justify-center text-center">
                                <p className="text-lg font-bold text-center">
                                    No images found in the library.
                                </p>
                                <br />
                                Images uploaded are automatically added.
                            </p>
                        </div>
                    ) : view === 'list' ? (
                        <LibraryList
                            files={filteredLibrary}
                            onImageSelected={setSelectedImage}
                        />
                    ) : (
                        <LibraryGrid
                            files={filteredLibrary}
                            onImageSelected={setSelectedImage}
                        />
                    )}
                    <div className="fade-to-background  absolute bottom-0 w-[calc(100%-2px)] h-[50px] z-10 ml-[1px] pr-[1px]"></div>
                </div>
            </div>
            <div
                className={cn(
                    'absolute bottom-[14px] right-[24px] w-[96%] flex justify-end z-2 transition-all duration-300',
                    selectedImage ? 'opacity-100' : 'opacity-0',
                )}
            >
                <button
                    className="dark-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (selectedImage) {
                            console.log('selectedImage', selectedImage);
                            onImageSelected(selectedImage);
                            onClose(e);
                        }
                    }}
                >
                    Select
                </button>
            </div>
        </div>
    );
}
